/**
 * Utility functions for managing owner users across multiple providers (companies).
 *
 * These helpers provide union-style semantics suitable for bulk editing:
 * - Build a union of all selected companies’ owner users.
 * - Toggle a user within that union.
 * - Query union membership and distribution.
 *
 * Design notes:
 * - Kept independent from UI/component modules to avoid circular dependencies.
 * - Uses a generic constraint to accept any objects with an optional `ownerUserIds?: number[]` field.
 * - Avoids `any` for type safety.
 */

/**
 * Sentinel value representing "no owner selected".
 * Used in UI lists to allow clearing all owners.
 */
export const NO_OWNER_SELECTED = 0

/**
 * Minimal shape for objects carrying owner users.
 */
export type OwnerUsersCarrier = {
  ownerUserIds?: number[]
}

/**
 * Computes the union of ownerUserIds across all selected providers.
 */
export function computeOwnerUsersUnion<T extends OwnerUsersCarrier>(providers: T[]): Set<number> {
  const unionSet = new Set<number>()
  for (const p of providers) {
    const list = p.ownerUserIds ?? []
    for (const id of list) unionSet.add(id)
  }
  return unionSet
}

/**
 * Toggles a user ID in the union. Returns the updated union as an array.
 */
export function toggleOwnerInUnion(unionSet: Set<number>, userId: number): number[] {
  if (unionSet.has(userId)) unionSet.delete(userId)
  else unionSet.add(userId)
  return Array.from(unionSet)
}

/**
 * Returns true if the given user ID exists in the union of owner users.
 */
export function isOwnerInUnion<T extends OwnerUsersCarrier>(providers: T[], userId: number): boolean {
  return computeOwnerUsersUnion(providers).has(userId)
}

/**
 * Returns the count of providers that currently have the given owner user.
 * For NO_OWNER_SELECTED, counts providers with no owners assigned.
 */
export function countProvidersWithOwner<T extends OwnerUsersCarrier>(providers: T[], userId: number): number {
  if (userId === NO_OWNER_SELECTED) {
    return providers.filter(p => (p.ownerUserIds?.length ?? 0) === 0).length
  }
  return providers.filter(p => (p.ownerUserIds ?? []).includes(userId)).length
}

/**
 * Determines if an owner option should be checked in the UI based on union membership.
 *
 * For NO_OWNER_SELECTED: returns true if the union is empty (no owners across all providers).
 * For a specific user ID: returns true if that user exists in the owner union.
 *
 * This is useful for multi-provider editing where checkboxes reflect union state.
 */
export function isOwnerOptionChecked<T extends OwnerUsersCarrier>(providers: T[], option: string | number): boolean {
  const unionSet = computeOwnerUsersUnion(providers)

  if (option === NO_OWNER_SELECTED) {
    return unionSet.size === 0
  }

  return unionSet.has(Number(option))
}

/**
 * Returns true if the current user is present in the union of owners across the given providers.
 *
 * Useful in UI logic to determine whether clicking on an option would remove the current user.
 */
export function isSelfInOwnerUnion<T extends OwnerUsersCarrier>(
  providers: T[],
  currentUserId: number | undefined,
): boolean {
  if (typeof currentUserId !== 'number') return false
  return computeOwnerUsersUnion(providers).has(currentUserId)
}

/**
 * Determines whether a confirmation should be shown when an owner-restricted user clicks an owner option.
 *
 * Triggers confirmation when:
 * - The clicked option equals the current user's id AND they currently belong to the owners union
 *   (i.e., toggling would remove themselves), OR
 * - The clicked option equals NO_OWNER_SELECTED AND the current user is in the owners union
 *   (i.e., clearing all owners would remove themselves).
 *
 * Parameters:
 * - option: the clicked option value (either a user id or NO_OWNER_SELECTED)
 * - providers: the current staged providers list used to compute the owners union
 * - currentUserId: the current user's id
 * - isOwnerRestricted: account flag indicating owner restriction
 */
export function shouldConfirmSelfOwnerRemoval<T extends OwnerUsersCarrier>(
  option: number,
  providers: T[],
  currentUserId: number | undefined,
  isOwnerRestricted: boolean,
): boolean {
  if (!isOwnerRestricted || typeof currentUserId !== 'number') return false

  const union = computeOwnerUsersUnion(providers)
  const isSelfCurrentlyOwner = union.has(currentUserId)
  if (!isSelfCurrentlyOwner) return false

  // Clearing all owners removes self if currently in the union
  if (option === NO_OWNER_SELECTED) return true

  // Clicking own user id while currently selected would unselect self
  return option === currentUserId
}

/**
 * Determines whether applying changes would remove the current user as owner from at least one provider.
 * Use this for a last-chance confirmation on "Apply".
 *
 * Strategy:
 * - If array ordering is stable, a position-wise diff is used.
 * - As a fallback (when ordering cannot be guaranteed), a count-based diff is used.
 *
 * Parameters:
 * - before: providers snapshot before editing (original state)
 * - after: providers snapshot currently staged (pending state)
 * - currentUserId: the current user's id
 * - isOwnerRestricted: account flag indicating owner restriction
 */
export function shouldConfirmApplySelfOwnerRemoval<T extends OwnerUsersCarrier>(
  before: T[],
  after: T[],
  currentUserId: number | undefined,
  isOwnerRestricted: boolean,
): boolean {
  if (!isOwnerRestricted || typeof currentUserId !== 'number') return false

  // Prefer position-wise comparison if arrays are aligned
  const minLen = Math.min(before.length, after.length)
  for (let i = 0; i < minLen; i += 1) {
    const hadSelf = (before[i].ownerUserIds ?? []).includes(currentUserId)
    const hasSelf = (after[i].ownerUserIds ?? []).includes(currentUserId)
    if (hadSelf && !hasSelf) return true
  }

  // Fallback to aggregate comparison if positions are unreliable
  const beforeSelfCount = before.filter(p => (p.ownerUserIds ?? []).includes(currentUserId)).length
  const afterSelfCount = after.filter(p => (p.ownerUserIds ?? []).includes(currentUserId)).length
  return afterSelfCount < beforeSelfCount
}
